import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { all, call, delay, put, select, take, takeEvery } from 'redux-saga/effects';
import { updateOrgAgencies } from '../actions/agencies';
import { updateAppsData } from '../actions/apps';
import { fetchedFields } from '../actions/fields';
import client from '../apollo';
import { updateAllFields } from '../sagas/fields';
import { getAppsList } from '../selectors/apps';
import { getFieldsInitiated } from '../selectors/fields';
import { getTranslate } from '../selectors/locale';
import AgenciesService from '../services/agencies';
import AppsAPI from '../services/apps';
import DataSourcesAPI from '../services/dataSources';
import FeaturesAPI from '../services/features';
import SectionsAPI from '../services/sections';
import {
    addMessageToGroup,
    CLOSE_AGENCY_DIALOG_CALLED,
    CLOSE_SHELF_CALLED,
    closeAgencyDialog,
    closeShelf,
    CONFIRM_CHANGES_ACCEPTED,
    CONFIRM_CHANGES_REJECTED,
    CREATE_NEW_MEMBER,
    DELETE_MEMBER_CLICKED,
    getOrgUsers,
    HIDE_PAGE_CALLED,
    hidePage,
    MANAGE_PERMISSIONS_CALLED,
    MANAGE_PERMISSIONS_FINISHED,
    managePermissions,
    modifyInvitationState,
    RESEND_INVITATION,
    saveMemberError,
    saveMemberSuccess,
    setInitialPermissionSnapshot,
    showConfirmChangesDialog,
    teamManagementLoaded,
    UPDATE_AGENCY_SELECTION_FORM,
    updateAppsList,
    updateDataSourceData,
    updateFeaturesDict,
    updatePartnersData,
    updateRoleSelectionForm,
    updateSectionsDict,
    userDisabled,
    userInviteRevoked,
} from './actions';
import { GET_BASE_SOURCE_PARTNERS } from './queries';
import { getActiveMemberType, getPermissionState, getWizardFinished, managePermissionsData } from './selectors';
import TeamManagementAPI from './service';
import {
    getRoleDisplayName,
    getStateFromUserDetails,
    getUserDisplayName,
    MEMBER_GROUP_TYPES,
    MEMBER_TYPES,
} from './utils';

const appsApi = new AppsAPI();
const dataSourcesApi = new DataSourcesAPI();
const sagas = new TeamManagementAPI();
const featuresApi = new FeaturesAPI();
const sectionsApi = new SectionsAPI();
const agenciesApi = new AgenciesService();

function* fetchOrgAgencies() {
    try {
        const agencies = yield call(agenciesApi.getOrgAgencies);
        yield put(updateOrgAgencies(agencies));
    } catch (e) {
        console.error(e);
        yield put(updateOrgAgencies([])); // or handle error appropriately
    }
}

function* updatePageData() {
    const initiated = yield select(getFieldsInitiated);

    if (!initiated) {
        yield call(updateAllFields);
    }

    const [usersResp, , dataResp, appsResp, partnersResp, featuresDictResp, sectionsDictResp] = yield all([
        call(sagas.getOrgUsers),
        call(fetchOrgAgencies), // dispatches to store internally
        call(dataSourcesApi.getBaseSources, {}),
        call(appsApi.getApps, true),
        call(client.query, { query: GET_BASE_SOURCE_PARTNERS }),
        call(featuresApi.getFeaturesDict),
        call(sectionsApi.getSectionsDict),
    ]);

    yield put(getOrgUsers(usersResp));
    yield put(updateAppsData(appsResp));
    yield put(updateDataSourceData(dataResp));
    yield put(updatePartnersData(partnersResp?.data?.baseSourcePartners || []));
    yield put(updateFeaturesDict(featuresDictResp));
    yield put(updateSectionsDict(sectionsDictResp));
    yield put(fetchedFields());
    yield put(teamManagementLoaded());

    const appsListResp = yield select(getAppsList);
    yield put(updateAppsList(appsListResp));
}

function* updateOrgUsers(disableBrowserCache) {
    const response = yield call(sagas.getOrgUsers, disableBrowserCache);
    yield put(getOrgUsers(response));
}

function* handleDeleteUser(user) {
    try {
        let group = '';

        if (user.status === 'Member') {
            const { result } = yield call(sagas.disableUser, user.organization_user_id);
            if (result) {
                yield put(userDisabled(user));
                group = 'active';
            }
        } else {
            const { result } = yield call(sagas.revokeInvitation, user.invite_id);
            if (result) {
                yield put(userInviteRevoked(user));
                group = 'pending';
            }
        }

        if (group) {
            const translate = yield select(getTranslate);
            yield put(
                addMessageToGroup(
                    group,
                    translate('STATIC.PAGES.TEAM_MEMBERS.USER_DELETED', { userName: getUserDisplayName(user) }),
                    'success'
                )
            );
        }
    } catch (e) {
        yield Promise.resolve();
    }
}

function* handleDeleteAgency(agency) {
    try {
        yield call(sagas.deleteOrgAgency, agency.id);
        const translate = yield select(getTranslate);
        yield put(
            addMessageToGroup(
                MEMBER_GROUP_TYPES.active,
                translate('STATIC.PAGES.TEAM_MEMBERS.AGENCY_DELETED', { agencyName: agency.name }),
                'success'
            )
        );
        yield call(fetchOrgAgencies);
    } catch (e) {
        yield put(saveMemberError(e.error));
    }
}

function* handleDeleteMemberClicked({ member }) {
    const activeMemberType = yield select(getActiveMemberType);

    if (activeMemberType === MEMBER_TYPES.AGENCY) {
        yield call(handleDeleteAgency, member);
    } else {
        yield call(handleDeleteUser, member);
    }
}

function* handleResendInvitation(action) {
    const { user } = action;
    try {
        yield put(modifyInvitationState(user, { inProgress: true, disabled: true }));
        const [{ result }] = yield all([call(sagas.resendInvitation, user.invite_id), delay(3000)]);
        if (result) {
            const translate = yield select(getTranslate);
            yield put(
                addMessageToGroup(
                    MEMBER_GROUP_TYPES.pending,
                    translate('STATIC.PAGES.TEAM_MEMBERS.RE_INVITE_SENT', { userName: getUserDisplayName(user) }),
                    'success'
                )
            );
            yield put(
                modifyInvitationState(user, {
                    inProgress: false,
                    disabled: true,
                    last_re_invited_at: moment.utc() / 1000,
                })
            );
        }
    } catch (e) {
        yield Promise.resolve();
    }
}

function* handleCreateNewUser(role, registrationForm, recaptchaToken) {
    const { type: roleType } = role;
    try {
        const { result } = yield call(sagas.sendInvitation, { ...registrationForm, role, recaptchaToken });
        if (result) {
            const translate = yield select(getTranslate);
            yield put(saveMemberSuccess());
            yield put(
                addMessageToGroup(
                    MEMBER_GROUP_TYPES.pending,
                    translate('STATIC.PAGES.TEAM_MEMBERS.INVITE_INFO', {
                        userName: getUserDisplayName(registrationForm, {
                            first_name: 'firstName',
                            last_name: 'lastName',
                        }),
                        role: getRoleDisplayName(roleType),
                    }),
                    'success'
                )
            );
            yield call(updateOrgUsers, true);
        }
    } catch (e) {
        yield put(saveMemberError(e.error));
    }
}

function* handleManageUserPermissions(editUserId, role, registrationForm) {
    try {
        const { result } = yield call(sagas.updateOrgUser, editUserId, role);
        if (result) {
            yield call(updateOrgUsers);
            const translate = yield select(getTranslate);
            yield put(saveMemberSuccess());
            yield put(
                addMessageToGroup(
                    'active',
                    translate('STATIC.PAGES.TEAM_MEMBERS.MODIFIED_TYPE_INFO', {
                        userName: getUserDisplayName(registrationForm, { first_name: 'name', last_name: 'lastname' }),
                        role: getRoleDisplayName(role.type),
                    }),
                    'success'
                )
            );
        }
    } catch (e) {
        yield put(saveMemberError(e.error));
    }
}

function* handleManageAgencyPermissions({ id, name }, role) {
    try {
        yield call(sagas.updateOrgAgency, id, role);
        yield call(fetchOrgAgencies);
        const translate = yield select(getTranslate);
        yield put(saveMemberSuccess());
        yield put(
            addMessageToGroup(
                MEMBER_GROUP_TYPES.active,
                translate('STATIC.PAGES.TEAM_MEMBERS.AGENCY_SAVED_MESSAGE', { agencyName: name }),
                'success'
            )
        );
    } catch (e) {
        yield put(saveMemberError(e.error));
    }
}
function* handleManagePermissionsFinished(action) {
    const activeMemberType = yield select(getActiveMemberType);
    const { editMemberId, registrationForm, role } = yield select(getWizardFinished);

    if (activeMemberType === MEMBER_TYPES.AGENCY) {
        yield call(handleManageAgencyPermissions, registrationForm, role);
        return;
    }

    if (editMemberId) {
        yield call(handleManageUserPermissions, editMemberId, role, registrationForm);
    } else {
        const { recaptchaToken } = action;
        yield call(handleCreateNewUser, role, registrationForm, recaptchaToken);
    }
}

function* confirmChanges(newPermission, oldPermission) {
    if (isEqual(newPermission, oldPermission)) {
        return true;
    }

    yield put(showConfirmChangesDialog());
    const action = yield take([CONFIRM_CHANGES_ACCEPTED, CONFIRM_CHANGES_REJECTED]);
    return action.type === CONFIRM_CHANGES_ACCEPTED;
}

function* handleShelfClose() {
    const permissionState = yield select(getPermissionState);
    const confirmed = yield call(confirmChanges, permissionState.currentState, permissionState.startState);
    if (confirmed) {
        yield put(closeShelf());
    }
}

function* handleHidePage() {
    const permissionState = yield select(getPermissionState);
    const confirmed = yield call(confirmChanges, permissionState.currentState, permissionState.startState);
    if (confirmed) {
        yield put(hidePage());
    }
}

function* handleCloseAgencyDialog() {
    const permissionState = yield select(getPermissionState);
    const confirmed = yield call(confirmChanges, permissionState.currentState, permissionState.startState);
    if (confirmed) {
        yield put(closeAgencyDialog());
    }
}

function* handleManagePermissionsCalled(action) {
    const { user } = action;
    yield put(updateRoleSelectionForm(user.role.type));
    const data = yield select(managePermissionsData);
    const partialState = getStateFromUserDetails(user, data);
    yield put(managePermissions(user, partialState));

    const snapshot = yield select(getWizardFinished);
    yield put(setInitialPermissionSnapshot(snapshot));
}

function* handleSnapshotPermissions() {
    const snapshot = yield select(getWizardFinished);
    yield put(setInitialPermissionSnapshot(snapshot));
}

// ---------------------------------/
//      WATCHERS
// ---------------------------------/

function* onload() {
    yield call(updatePageData);
}

function* watchDeleteUserClicked() {
    yield takeEvery(DELETE_MEMBER_CLICKED, handleDeleteMemberClicked);
}

function* watchResendPermissions() {
    yield takeEvery(RESEND_INVITATION, handleResendInvitation);
}

function* watchManagePermissionsFinished() {
    yield takeEvery(MANAGE_PERMISSIONS_FINISHED, handleManagePermissionsFinished);
}

function* watchShelfClose() {
    yield takeEvery(CLOSE_SHELF_CALLED, handleShelfClose);
}

function* watchHidePage() {
    yield takeEvery(HIDE_PAGE_CALLED, handleHidePage);
}

function* watchCloseAgencyDialog() {
    yield takeEvery(CLOSE_AGENCY_DIALOG_CALLED, handleCloseAgencyDialog);
}

function* watchManagePermissionsCalled() {
    yield takeEvery(MANAGE_PERMISSIONS_CALLED, handleManagePermissionsCalled);
}

function* watchCreateNewMember() {
    yield takeEvery(CREATE_NEW_MEMBER, handleSnapshotPermissions);
}

function* watchAgencySelected() {
    yield takeEvery(UPDATE_AGENCY_SELECTION_FORM, handleSnapshotPermissions);
}

const watchers = [
    watchDeleteUserClicked,
    watchResendPermissions,
    watchManagePermissionsFinished,
    watchShelfClose,
    watchHidePage,
    watchManagePermissionsCalled,
    watchCloseAgencyDialog,
    watchCreateNewMember,
    watchAgencySelected,
];

export { onload, watchers };
